import type { CompanyPatchPayload } from "@repo/app-types/entities/company";
import { defaultReapplyPolicy } from "@repo/app-types/entities/company";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import type { FC } from "react";
import { useUpdateCompanyMutation } from "../../../api/entities/company.js";
import { DatesTable } from "../../../components/datesTable.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { useAppSelector } from "../../../hooks/hook.js";
import {
  selectCompany,
  selectIsRecruitingModeCompany,
} from "../../company/companySlice.js";
import { WelcomeAndOutroMessagesEditor } from "../../projects/projectSettings/welcomeAndOutroMessagesEditor.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsSectionPage } from "../shared/settingsSection.js";
import { CompanyColorPicker } from "./companyColorPicker.js";
import { CompanyInterviewerNamesEditor } from "./companyInterviewerNamesEditor.js";
import { CompanyImageEditor } from "./companyLogoEditor.js";
import { CompanyNameDescriptionEditor } from "./companyNameDescriptionEditor.js";
import { CompanyReapplyPolicyEditor } from "./companyReapplyPolicyEditor.js";
import { CompanyTimezoneEditor } from "./companyTimezoneEditor.js";

const CompanySettingsSection: FC = () => {
  const company = useAppSelector(selectCompany);
  const isRecruitingModeCompany = useAppSelector(selectIsRecruitingModeCompany);
  const isRecruitingEnabled = !useIsFeatureFlagEnabled(
    FeatureFlagKey.HideHireTab,
  );
  const isTrainingEnabled = useIsFeatureFlagEnabled(FeatureFlagKey.Training);
  const [updateCompany, { isLoading: isUpdatingCompany }] =
    useUpdateCompanyMutation();

  if (!company) {
    return null;
  }

  const onSave = async (updates: CompanyPatchPayload): Promise<boolean> => {
    try {
      await updateCompany(updates).unwrap();

      showToast({
        title: `Organization successfully updated`,
        variant: ToastVariant.Success,
      });
      return true;
    } catch (error) {
      console.error("Failed to update company", error);
      showToast({
        title: `Failed to update organization`,
        variant: ToastVariant.Error,
      });
      return false;
    }
  };

  const shouldShowBrandingSettings = isRecruitingEnabled || isTrainingEnabled;
  const brandingPageDescription = isTrainingEnabled
    ? "training pages"
    : "interview page";

  return (
    <SettingsSectionPage title={settingsSections.organization.label}>
      <div className="flex flex-col space-y-8">
        <CompanyNameDescriptionEditor
          onSave={onSave}
          name={company.name}
          description={company.description}
        />

        <Can
          I={PermissionActions.UPDATE}
          a={PermissionSubjects.COMPANY_SETTINGS}
          passThrough={true}
        >
          {(allowed) => (
            <CompanyTimezoneEditor
              timezone={company.settings.timezone ?? null}
              isDisabled={!allowed || isUpdatingCompany}
              onSave={onSave}
            />
          )}
        </Can>

        {shouldShowBrandingSettings && (
          <div className="flex flex-col space-y-6">
            <div>
              <Label size={Size.SMALL}>Logo</Label>
              <CompanyImageEditor company={company} type="logo" />
            </div>

            <div>
              <Label size={Size.SMALL}>Favicon</Label>
              <CompanyImageEditor company={company} type="favicon" />
            </div>

            <div>
              <Label size={Size.SMALL} className="mb-2 block">
                Background color
              </Label>
              <CompanyColorPicker
                color={company.settings.interview_color || "#172554"} // Default blue-950
                onChange={(color) => {
                  onSave({
                    settings: { interview_color: color },
                  });
                }}
              />
              <div className="mt-2 text-sm text-muted-foreground">
                {`Customize the background color of your ${brandingPageDescription} to match your brand.`}
              </div>
            </div>
          </div>
        )}

        {isRecruitingEnabled && (
          <Can
            I={PermissionActions.UPDATE}
            a={PermissionSubjects.COMPANY_SETTINGS}
            passThrough={true}
          >
            {(allowed) => (
              <div className="flex flex-col space-y-8">
                <WelcomeAndOutroMessagesEditor
                  welcomeMessage={company.settings.welcome_message}
                  outroMessage={company.settings.outro_message}
                  onWelcomeMessageSave={(message) => {
                    onSave({
                      settings: { welcome_message: message },
                    });
                  }}
                  onOutroMessageSave={(message) => {
                    onSave({
                      settings: { outro_message: message },
                    });
                  }}
                  isProjectLevel={false}
                  isDisabled={!allowed}
                />
                <CompanyInterviewerNamesEditor
                  interviewerNames={company.settings.interviewer_names}
                  isDisabled={!allowed || isUpdatingCompany}
                  onSave={onSave}
                />
                {isRecruitingModeCompany && (
                  <>
                    <CompanyReapplyPolicyEditor
                      reapplyPolicy={
                        company.settings.reapply_policy || defaultReapplyPolicy
                      }
                      onSave={(reapplyPolicy) => {
                        onSave({
                          settings: { reapply_policy: reapplyPolicy },
                        });
                      }}
                    />

                    <DatesTable
                      dates={
                        company.settings.scheduling_availability
                          ?.blocked_dates || []
                      }
                      onChange={(dates) => {
                        onSave({
                          settings: {
                            scheduling_availability: {
                              blocked_dates: dates,
                            },
                          },
                        });
                      }}
                      title="Blocked dates"
                      description="Dates on which candidates cannot schedule their next interview. These dates apply across all job openings."
                      isDisabled={!allowed}
                    />
                  </>
                )}
              </div>
            )}
          </Can>
        )}
      </div>
    </SettingsSectionPage>
  );
};

export default CompanySettingsSection;
